import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SortDirection as SortDirectionEnum } from '@/enums/sort-direction.enum'
import { ToolStatus } from '@/enums/tool-status.enum'
import { useGetTrolleySummary } from '@/hooks/useToolManagement/useGetTrolleySummary'
import { useLoading } from '@/contexts/LoadingContext'

const ITEMS_PER_PAGE = 10

type StatusFilter = Nullable<ToolStatus>
type SortColumn = Nullable<'name' | 'description' | 'quantity'>
type SortDirection = Nullable<SortDirectionEnum>

export function useSummarizationPage() {
  const { t } = useTranslation()
  const { showLoading, hideLoading } = useLoading()
  const [searchText, setSearchText] = useState('')
  const [searchQuery, setSearchQuery] = useState<Nillable<string>>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortColumn, setSortColumn] = useState<SortColumn>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  const {
    items: summaries,
    total_pages,
    isLoading,
    error,
  } = useGetTrolleySummary({
    page: currentPage,
    page_size: ITEMS_PER_PAGE,
    search: searchQuery,
    status: statusFilter ?? undefined,
    sort_by: sortColumn ?? undefined,
    sort_order: sortDirection ?? undefined,
  })

  useEffect(() => {
    const shouldShowLoading = isLoading && searchText.trim().length === 0

    if (shouldShowLoading) {
      showLoading(t('common.loading'))
    } else {
      hideLoading()
    }
  }, [isLoading, showLoading, hideLoading, t, searchText])

  useEffect(() => {
    if (currentPage > total_pages && total_pages > 0) {
      setCurrentPage(1)
    }
  }, [total_pages, currentPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter])

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === SortDirectionEnum.Asc) {
        setSortDirection(SortDirectionEnum.Desc)
      } else if (sortDirection === SortDirectionEnum.Desc) {
        setSortColumn(null)
        setSortDirection(null)
      } else {
        setSortDirection(SortDirectionEnum.Asc)
      }
    } else {
      setSortColumn(column)
      setSortDirection(SortDirectionEnum.Asc)
    }
    setCurrentPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearchText(value)

    const trimmed = value.trim()
    if (trimmed.length === 0) {
      if (searchQuery !== null) {
        setSearchQuery(null)
        setCurrentPage(1)
      }
      return
    }

    if (trimmed.length < 3) {
      if (searchQuery !== null) {
        setSearchQuery(null)
        setCurrentPage(1)
      }
      return
    }

    if (trimmed !== searchQuery) {
      setSearchQuery(trimmed)
      setCurrentPage(1)
    }
  }

  return {
    searchTerm: searchText,
    setSearchTerm: handleSearchChange,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    summaries,
    totalPages: total_pages,
    isLoading,
    error,
    sortColumn,
    sortDirection,
    onSort: handleSort,
  }
}
